import formatDate from 'dateformat';
import { v4 as uuidv4 } from 'uuid';
import { MATFILE_ENDLESS_TYPE } from 'pxc-editor-core/constants';
import { safeBtoa } from 'pxc-editor-core/helpers/common';
import store from '@/store';
import { ENDLESS_FIXED, ENDLESS_BASED_ON_CONTENT, SECTION_CONTENT_TYPE } from '@/constants';
import { getElementByGetElementById } from '@/helpers';
import { convertValueToPercent } from '@/helpers/graphicContent';
import { getBuildedMatfileTemplate } from '@/helpers/matfile';
import { getOriginMatFileMetadata, getOriginInitFullProjectData, getCreatedDate, getOriginWizardPrintOptions, getOriginPagesSeparators, getInitProjectData } from './parser';
import { toProjFileContent as barcodeToProjFileContent } from './barcodeInOutMapper';
import getVersionControl from 'EnvironmentModule/helpers/getVersionControl';

export const generateDataToDownload = ({ isSave = true, previewImg = null }) => {
  const originInitProjectData = getOriginInitFullProjectData();
  const { core } = store.getState();
  const data = {
    ...originInitProjectData,
    project_metadata: generateProjectMetadata(),
    project: generateProjectOuterData({ isSave }),
    preview: previewImg,
    ...(core?.selWizard && { wizard: safeBtoa(JSON.stringify(core.selWizard)) })
  };
  const project_version = generateProjectVersion(data);

  return { ...data, project_version };
};

export const generateRequestRenderedImageBody = (printSettings, imageUrlData, sizeMM, seq, total, original_page_number, { projectName }, tile_seq = null, tile_total = null) => {
  const {
    page_selection: { copies, collate },
    print_job: { print_job_id, printer_id },
    printer_options,
    material_metadata,
  } = printSettings;
  const {
    type,
    printParameters: { printer_models },
  } = getBuildedMatfileTemplate();

  let pageSeparatorConfig = null;

  if (type === MATFILE_ENDLESS_TYPE) {
    pageSeparatorConfig = getPageSeparatorConfigs(total);
  }

  return {
    print_job_id,
    printer_id,
    seq,
    total,
    tile_seq,
    tile_total,
    copies,
    collate,
    original_page_number,
    document_name: projectName || 'New Project',
    material_metadata,
    page_size: sizeMM,
    material_print_params: printer_models,
    page_separator_config: pageSeparatorConfig,
    printer_options,
    image: imageUrlData,
  };
};

export const generateRequestOpenPrintDialogData = ({ totalPages, currentPage }) => {
  const originMaterialMetadata = getOriginMatFileMetadata();
  const {
    type,
    printParameters: { printer_models },
  } = getBuildedMatfileTemplate();
  let pageSeparatorConfig = null;

  if (type === MATFILE_ENDLESS_TYPE) {
    pageSeparatorConfig = getPageSeparatorConfigs(totalPages);
  }

  return {
    total_pages: totalPages,
    current_page: currentPage,
    material_metadata: originMaterialMetadata,
    material_print_params: printer_models,
    page_separator_config: pageSeparatorConfig,
  };
};

const getPageSeparatorConfigs = pagesCount => {
  let res = null;
  const originWizardPrintOptions = getOriginWizardPrintOptions();
  const originPagesSeparators = getOriginPagesSeparators();

  if (originPagesSeparators) {
    res = originPagesSeparators.slice(0, pagesCount - 1);
    if (res.length < pagesCount - 1) {
      for (let i = res.length; i < pagesCount - 1; i++) {
        res.push({ type: 'NONE', space_length: 0 });
      }
    }
  } else if (originWizardPrintOptions && originWizardPrintOptions.separators && originWizardPrintOptions.separators.length) {
    res = [];
    for (let i = 1; i < pagesCount; i++) {
      const item = { type: 'NONE', space_length: 0 };
      let setFor = 0;
      originWizardPrintOptions.separators.forEach(({ separator_type, between_each, space_length }) => {
        if (i % between_each === 0 && setFor <= between_each) {
          item.type = separator_type;
          item.space_length = space_length;
          setFor = between_each;
        }
      });
      res.push(item);
    }
  }

  return res;
};

const generateProjectMetadata = () => {
  const { core: { pages, pagesOrder, preselectedPrinter } } = store.getState();
  const { file_name, type } = getBuildedMatfileTemplate();
  let labels = 0;
  let totalLength = 0;

  for (const puid in pages) {
    const { sections, box } = pages[puid];

    if (type === MATFILE_ENDLESS_TYPE) {
      totalLength += box.r;
    }

    for (const sectionId in sections) {
      const section = sections[sectionId];
      if (section.code || section.image || section.glyph || (section.text && !!section.text.trim().length)) {
        labels++;
      }
    }
  }

  const updated = formatDate(new Date(), 'isoDateTime');
  const created = getCreatedDate() || updated;
  return {
    source: 'EDITOR',
    material_filename: file_name,
    template_filename: '',
    material_metadata: getOriginMatFileMetadata(),
    stats: {
      pages: pagesOrder.length,
      labels,
      total_length: Math.ceil(totalLength), // TODO: wrong solution. need to pass raw value and change in docs type to 'number'
      created,
      updated,
    },
    preselectedPrinter,
  };
};

const generateProjectOuterData = ({ isSave }) => {
  const {
    core: { laserOutputQuality },
  } = store.getState();
  const timeNow = formatDate(new Date(), 'isoDateTime');
  const globalSectionSettings = generateGlobalSectionSettings();
  //*** project base setting ******
  const project = {
    created: getCreatedDate() || timeNow,
    updated: timeNow,
    format_version: 1,
    is_template: false,
    text_styles: null,
    materials: null,
    global_laser_settings: { laser_output_quality: laserOutputQuality },
    global_settings: generateGlobalProjectSettings({ globalSectionSettings })
  };
  const globTextStyles = globalSectionSettings ? [globalSectionSettings] : [];

  project.materials = [generateProjectOuterMaterialData({ isSave, globTextStyles })];
  project.text_styles = globTextStyles;

  return safeBtoa(JSON.stringify({ project }));
};

const generateProjectOuterMaterialData = ({ isSave, globTextStyles }) => {
  const { core: { pages, pagesOrder, colorSchema } } = store.getState();
  const { name, file_name, type } = getBuildedMatfileTemplate();
  const initProjectData = getInitProjectData();
  //*** material base setting ******
  const material = {
    id: initProjectData?.project?.materials[0]?.id || uuidv4(),
    material_filename: file_name,
    template_filename: null,
    material_name: name,
    variant_article: colorSchema?.article_number,
    variant_name: colorSchema?.article_name,
    invert_colors: false,
    layout_name: null, // use first layout name
    separators: null,
    pages: [],
  };

  //*** material.separators setting ******
  if (type === MATFILE_ENDLESS_TYPE) {
    material.separators = getPageSeparatorConfigs(pagesOrder.length);
  }

  //*** material.pages setting ******
  pagesOrder.forEach(pageUid => {
    //*** page base setting ******
    if (!material.layout_name) {
      for (const layoutUid in pages[pageUid].layouts) {
        material.layout_name = pages[pageUid].layouts[layoutUid].name;
        break;
      }
    }
    material.pages.push(generateProjectOuterPageData({ isSave, appPage: pages[pageUid], materialId: material.id, globTextStyles }));
  });

  return material;
};

const generateProjectOuterPageData = (payload) => {
  const { isSave, appPage, materialId, globTextStyles } = payload;
  const matfileTemplate = getBuildedMatfileTemplate();

  const resPage = {
    id: appPage.uid,
    material_id: materialId,
    custom_length: null,
    transforms: { ...appPage.transforms },
    labels: [],
  };

  if (matfileTemplate.type === MATFILE_ENDLESS_TYPE) {
    resPage.endless_options = {
      adaptive_length: appPage.resizeType !== ENDLESS_FIXED,
    };
    resPage.endless_options.custom_length = appPage.box.r;
  }

  const sectionsByShields = {};
  for (const sectionUid in appPage.sections) {
    const section = appPage.sections[sectionUid];
    sectionsByShields[section.shieldId] = sectionsByShields[section.shieldId] || [];
    sectionsByShields[section.shieldId].push(section);
  }

  for (const layoutUid in appPage.layouts) {
    const { groups } = appPage.layouts[layoutUid];
    for (const groupUid in groups) {
      const { shields, id: originGroupId } = groups[groupUid];
      for (const shieldUId in shields) {
        const shield = shields[shieldUId];
        //*** label base setting ******
        const resLabel = {
          group_id: originGroupId,
          shield_id: shield.shield_id,
          is_matrix: shield.isMatrix,
          is_fixed_matrix: shield.isMatrixFixedCellWidth,
          is_matrix_borders_enabled: shield.isMatrixBordersEnabled,
          style: {
            outline: shield.primitiveProps.outline_style,
            background: shield.primitiveProps.background_style,
          },
          sections: [],
        };

        if (sectionsByShields[shieldUId]) {
          sectionsByShields[shieldUId].forEach(section => {
            resLabel.sections.push(generateProjectOuterSectionData({ isSave, section, globTextStyles }));
          });
        }

        resPage.labels.push(resLabel);
      }
    }
  }
  return resPage;
};

const generateProjectOuterSectionData = ({ isSave, section, globTextStyles }) => {
  const backgroundStyle = {
    type: 'FILL',
    enabled: section.hasBackground,
    fill_color: section.fillColor,
  };
  const scalePercentage = convertValueToPercent(section.graphicFormattingScale) / 100;
  const resSection = {
    id: section.id,
    is_from_template: false,
    contour: {
      primitive_type: 'RECT',
      rect: {
        x: section.x,
        y: section.y,
        w: section.width,
        h: section.height,
        roundedCornerRadius: 0,
        ...section.hasBackground && { background_style: backgroundStyle },
      },
    },
    type: null,
    laser_options: { cut_out_contour: !!section?.laserOutputQuality?.cutout },
    isChanged: section.isChanged,
    graphic_contents_options: {
      scale_percentage: scalePercentage,
    }
  };

  if (section.code) {
    resSection.type = SECTION_CONTENT_TYPE.BARCODE;
    resSection.barcode_contents = barcodeToProjFileContent(section.code);
    resSection.barcode_contents.style = {
      color: section.barcodeColor,
      transforms: {
        angle: section.angle,
        mirror_x: section.mirrorX,
        mirror_y: section.mirrorY,
      },
    };
  } else if (section.image) {
    const imageElement = getElementByGetElementById(`${section.id}-image`);
    const x = imageElement?.x?.baseVal?.value || section.image.x || 0;
    const y = imageElement?.y?.baseVal?.value || section.image.y || 0;
    const imageHref = section.image.href;
    const imageSource = isSave ? imageHref : section.image.source;
    const contrastStyle = isSave ? {} : { contrast: { enabled: section.image.contrast, value: section.image.contrastValue } };

    resSection.type = SECTION_CONTENT_TYPE.IMAGE;
    resSection.image_contents = {
      image_file_name: section.image.name,
      image_source: imageSource, // this field is not in the schema
      image_href: imageHref,
      vertical_alignment: section.image.verticalAlignment,
      horizontal_alignment: section.image.horizontalAlignment,
      x,
      y,
      style: {
        transforms: {
          angle: section.angle,
          mirror_x: section.mirrorX,
          mirror_y: section.mirrorY,
        },
        ...contrastStyle, // this field is not in the schema
      },
    };
  } else if (section.glyph) {
    resSection.type = SECTION_CONTENT_TYPE.SYMBOL;
    resSection.symbol_contents = {
      symbol: section.glyph.unicode,
      symbol_font: section.symbolFont,
      vertical_alignment: section.glyph.verticalAlignment,
      horizontal_alignment: section.glyph.horizontalAlignment,
      style: {
        color: section.glyphFillColor,
        transforms: {
          angle: section.angle,
          mirror_x: section.mirrorX,
          mirror_y: section.mirrorY,
        },
      },
    };
  } else if (section.shape) {
    resSection.type = SECTION_CONTENT_TYPE.SHAPE;
    resSection.shape_contents = {
      shape_type: section.shape.name,
      outline_style: {
        enabled: section.shape.hasStroke,
        color: section.shapeStrokeColor,
        line_width: section.shape.strokeWidth,
        line_type: section.shape.isDashed ? 'DASHED' : 'SOLID',
        corners: section.shape.softEdges ? 'ROUND' : 'RECTANGULAR',
        round_corners_radius: section.shape.cornerRadius,
      },
      background_style: {
        enabled: section.shape.isFilled,
        type: 'FILL',
        fill_color: section.shapeFillColor,
        image_file: '',
      }
    };
  } else if (section.type === 'separator') {
    resSection.type = 'SEPARATOR';
  } else {
    resSection.type = SECTION_CONTENT_TYPE.TEXT;
    const secTextStyle = {
      id: `${resSection.id}-section`,
      font_name: section.font,
      text_height: section.fontSize,
      italic: section.isItalic,
      bold: section.isBold,
      underline: section.isUnderline,
      color: section.fontColor,
      vertical_alignment: section.verticalAlignment.toUpperCase(),
      horizontal_alignment: section.horizontalAlignment.toUpperCase(),
      transforms: {
        angle: section.angle,
        mirror_x: section.mirrorX,
        mirror_y: section.mirrorY,
      },
    };
    resSection.text_contents = {
      text: section.text,
      style_id: secTextStyle.id,
    };
    globTextStyles.push(secTextStyle);
  }

  return resSection;
};

const generateGlobalSectionSettings = () => {
  const { appSettingsReducer } = store.getState();
  const sectionSettings = Object.entries(appSettingsReducer.section).reduce((acc, [key, value]) => {
    switch (key) {
      case 'isBold': {
        return { ...acc, bold: value };
      }
      case 'isItalic': {
        return { ...acc, italic: value };
      }
      case 'isUnderline': {
        return { ...acc, underline: value };
      }
      case 'fontSize': {
        return { ...acc, text_height: value };
      }
      case 'font': {
        return { ...acc, font_name: value };
      }
      case 'fontColor': {
        return { ...acc, color: value };
      }
      default: {
        return acc;
      }
    }
  }, {});

  if (Object.keys(sectionSettings).length) {
    return { id: 'global-settings', ...sectionSettings };
  }
  return null;
};

const generateGlobalProjectSettings = (data) => {
  const { globalSectionSettings } = data;
  const { appSettingsReducer: { page } } = store.getState();
  const pageSettings = Object.entries(page).reduce((acc, [key, value]) => {
    switch (key) {
      case 'resizeType': {
        return { ...acc, endless_page_default_is_adaptive_length: value === ENDLESS_BASED_ON_CONTENT };
      }
      case 'length': {
        return { ...acc, endless_page_default_length: value };
      }
      default: {
        return acc;
      }
    }
  }, {});

  return {
    last_used_text_style_id: globalSectionSettings?.id,
    ...pageSettings,
  }
};

const generateProjectVersion = (data) => {
  switch (process.env.REACT_APP_TARGET) {
    case 'xcore': {
      return safeBtoa(JSON.stringify(getVersionControl(data)));
    }
    default: {
      return getVersionControl(data);
    }
  }
};